import axios from "axios";
import { Loader2 } from "lucide-react";
import React, { useState } from "react";
import { toast } from "sonner";
import EditorTextArea from "../../../components/EditorTextArea";
import FileUploadImage from "../../../components/FileUploadImage";
import FileUploadVideo from "../../../components/FileUploadVideo";
import { Button } from "../../../components/ui/button";
import { Card, CardContent } from "../../../components/ui/card";
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from "../../../components/ui/drawer";
import { Input } from "../../../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../components/ui/select";
import { useLanguage } from "../../../context/LanguageContext";
import { BaseUrlApi, ErrorMessage } from "../../../lib/api";
import { CategoriesData, CourseData } from "../../../lib/languages";

const DrawerAddCourse = ({ getAllCourses, item, type }) => {
  const { language } = useLanguage();
  const t = CourseData[language] || CourseData["en"];

  const [open, setOpen] = useState(false);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [image, setImage] = useState(null);
  const [bigImage, setBigImage] = useState(null);
  const [video, setVideo] = useState(null);
  const [form, setForm] = useState({
    title_en: "",
    title_ar: "",
    s_description_en: "",
    s_description_ar: "",
    b_description_en: "",
    b_description_ar: "",
    status: false,
    type: false,
  });

  const handleAddCourse = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const formData = new FormData();

      if (form.title_ar || form.title_en) {
        formData.append(
          "title",
          JSON.stringify({ ar: form.title_ar, en: form.title_en })
        );
      }

      if (form.s_description_ar || form.s_description_en) {
        formData.append(
          "description",
          JSON.stringify({
            ar: form.s_description_ar,
            en: form.s_description_en,
          })
        );
      }

      if (form.b_description_ar || form.b_description_en) {
        formData.append(
          "big_description",
          JSON.stringify({
            ar: form.b_description_ar,
            en: form.b_description_en,
          })
        );
      }

      if (image) formData.append("image", image);
      if (bigImage) formData.append("big_image", bigImage);
      if (video) formData.append("video", video);

      formData.append("status", form.status ? 1 : 0);
      formData.append("type", form.type ? 1 : 0);
      if (type === "category") {
        formData.append("category_id", item.id);
      } else if (type === "sub_category") {
        formData.append("sub_category_id", item.id);
      }

      await axios.post(`${BaseUrlApi}/courses`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      toast(t.courseCreatedToast, {
        description: t.courseCreatedDescription,
      });

      getAllCourses();
      setOpen(false);
      ClearAll();
    } catch (error) {
      setErrors(ErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  const ClearAll = () => {
    setImage(null);
    setErrors({});
    setForm({
      title_en: "",
      title_ar: "",
      description_en: "",
      description_ar: "",
      status: false,
    });
  };

  return (
    <Drawer open={open} onOpenChange={setOpen}>
      <DrawerTrigger asChild>
        <Button className="h-10 bg-blue-600 text-white hover:bg-blue-600/80 rounded-xl">
          {t.addCourse}
        </Button>
      </DrawerTrigger>
      <DrawerContent dir={language === "ar" ? "rtl" : "ltr"}>
        <div className="h-[calc(100vh-2rem)] max-w-7xl mx-auto overflow-auto hide-scrollbar space-y-4 p-4">
          {/* Header */}
          <DrawerHeader className="flex items-center justify-between flex-wrap gap-4 p-0 pb-2">
            <div className="w-full sm:w-fit">
              <DrawerTitle className="font-bold text-2xl">
                {t.addCourse}
              </DrawerTitle>
              <DrawerDescription className="font-medium text-sm">
                {t.easilyCreateAndManageCourse}
              </DrawerDescription>
            </div>
            <div className="flex items-center gap-2 w-full sm:w-fit">
              <Button
                className="w-full sm:w-fit h-10 bg-blue-600 text-white hover:bg-blue-600/80 rounded-xl z-10"
                onClick={handleAddCourse}
                disabled={loading}
              >
                {loading && (
                  <div className="animate-spin">
                    <Loader2 />
                  </div>
                )}
                {t.addCourse}
              </Button>
              <DrawerClose asChild>
                <Button
                  variant="outline"
                  className="w-full sm:w-fit h-10 border-2 border-blue-600 text-blue-600 hover:text-blue-500 rounded-xl"
                >
                  {t.cancel}
                </Button>
              </DrawerClose>
            </div>
          </DrawerHeader>
          {/* Uploads */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 relative">
            {/* Upload Thumbnail */}
            <Card>
              <CardContent className="p-4 space-y-4 flex flex-col h-full">
                <p className="font-bold text-xl">{t.thumbnail}</p>
                <FileUploadImage
                  onUploadComplete={(url) => setImage(url)}
                  name={"courses"}
                />
                {errors?.image && (
                  <p className="text-red-500 font-semibold text-sm">
                    {errors.image[0]}
                  </p>
                )}
                <div className="font-normal text-sm text-muted-foreground text-center">
                  {t.imageExtensionInfo}
                </div>
              </CardContent>
            </Card>
            {/* Upload Big Image */}
            <Card>
              <CardContent className="p-4 space-y-4 flex flex-col h-full">
                <p className="font-bold text-xl">{t.bigImage}</p>
                <FileUploadImage
                  onUploadComplete={(url) => setBigImage(url)}
                  name={"courses"}
                />
                {errors?.big_image && (
                  <p className="text-red-500 font-semibold text-sm">
                    {errors.big_image[0]}
                  </p>
                )}
                <div className="font-normal text-sm text-muted-foreground text-center">
                  {t.imageExtensionInfo}
                </div>
              </CardContent>
            </Card>
            {/* Upload Video */}
            <Card>
              <CardContent className="p-4 space-y-4 flex flex-col h-full">
                <p className="font-bold text-xl">{t.video}</p>
                <FileUploadVideo
                  onUploadComplete={(url) => setVideo(url)}
                  name={"courses"}
                />
                {errors?.video && (
                  <p className="text-red-500 font-semibold text-sm">
                    {errors.video[0]}
                  </p>
                )}
                <div className="font-normal text-sm text-muted-foreground text-center">
                  {t.videoExtensionInfo}
                </div>
              </CardContent>
            </Card>
          </div>
          {/* General Information */}
          <Card>
            <CardContent className="p-4 space-y-4 flex-1 flex flex-col">
              <p className="font-bold text-xl border-b pb-2">
                {t.generalInformation}
              </p>
              <div className="space-y-4 flex-1 flex flex-col">
                <div className="flex items-center gap-4">
                  <div className="flex flex-col gap-2 w-full">
                    <label htmlFor="title_en" className="text-sm">
                      {t.titleEn}
                    </label>
                    <Input
                      id="title_en"
                      name="title_en"
                      type="text"
                      placeholder={t.enterTitleEn}
                      onChange={(e) =>
                        setForm({ ...form, title_en: e.target.value })
                      }
                    />
                    {errors?.title && (
                      <p className="text-red-500 font-semibold text-sm">
                        {errors.title[0]}
                      </p>
                    )}
                  </div>
                  <div className="flex flex-col gap-2 w-full">
                    <label htmlFor="title_ar" className="text-sm">
                      {t.titleAr}
                    </label>
                    <Input
                      id="title_ar"
                      name="title_ar"
                      type="text"
                      placeholder={t.enterTitleAr}
                      onChange={(e) =>
                        setForm({ ...form, title_ar: e.target.value })
                      }
                    />
                    {errors?.title && (
                      <p className="text-red-500 font-semibold text-sm">
                        {errors.title[0]}
                      </p>
                    )}
                  </div>
                </div>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  {/* Small Description */}
                  <div className="flex flex-col gap-2 flex-1">
                    <label htmlFor="description" className="text-sm">
                      {t.smallDescription}
                    </label>
                    <EditorTextArea
                      form={form}
                      en="s_description_en"
                      ar="s_description_ar"
                      setForm={setForm}
                    />
                  </div>
                  {/* Big Description */}
                  <div className="flex flex-col gap-2 flex-1">
                    <label htmlFor="description" className="text-sm">
                      {t.bigDescription}
                    </label>
                    <EditorTextArea
                      form={form}
                      en="b_description_en"
                      ar="b_description_ar"
                      setForm={setForm}
                    />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Status */}
            <Card>
              <CardContent className="p-4 space-y-4">
                <div className="flex items-center gap-4 justify-between border-b pb-2">
                  <p className="font-bold text-xl">{t.status}</p>
                  <div
                    className={`size-4 ${
                      form?.status ? "bg-green-500" : "bg-red-500"
                    } rounded-full`}
                  />
                </div>
                <div className="grid gap-2">
                  <p className="text-sm">{t.selectStatus}</p>
                  <Select
                    value={form?.status}
                    onValueChange={(e) => setForm({ ...form, status: e })}
                  >
                    <SelectTrigger
                      className="rounded-lg sm:ml-auto"
                      aria-label={t.selectStatus}
                    >
                      <SelectValue placeholder={t.selectStatus} />
                    </SelectTrigger>
                    <SelectContent className="rounded-xl">
                      <SelectItem value={true} className="rounded-lg">
                        {t.active}
                      </SelectItem>
                      <SelectItem value={false} className="rounded-lg">
                        {t.inactive}
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </CardContent>
            </Card>
            {/* Type */}
            <Card>
              <CardContent className="p-4 space-y-4">
                <div className="flex items-center gap-4 justify-between border-b pb-2">
                  <p className="font-bold text-xl">{t.selectType}</p>
                  <div
                    className={`relative size-4 ${
                      form?.type ? "bg-destructive" : "bg-green-500"
                    } rounded-full`}
                  >
                    {form?.type && (
                      <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-destructive opacity-75" />
                    )}
                  </div>
                </div>
                <div className="grid gap-2">
                  <p className="text-sm">{t.selectType}</p>
                  <Select
                    value={form?.type}
                    onValueChange={(e) => setForm({ ...form, type: e })}
                  >
                    <SelectTrigger
                      className="rounded-lg sm:ml-auto"
                      aria-label={t.selectType}
                    >
                      <SelectValue placeholder={t.selectType} />
                    </SelectTrigger>
                    <SelectContent className="rounded-xl">
                      <SelectItem value={true} className="rounded-lg">
                        {t.live}
                      </SelectItem>
                      <SelectItem value={false} className="rounded-lg">
                        {t.recorded}
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </DrawerContent>
    </Drawer>
  );
};

export default DrawerAddCourse;
